import "./assessment-page.scss";

import { observer } from "mobx-react";
import React from "react";
import { generatePath, Link, useHistory } from "react-router-dom";

import { AssessmentAPI } from "@/api/legacy/risk-assessment/AssessmentApi";
import ExportBulk from "@/components/dashboard/ExportBulk";
import { Flex, Icon, Typography } from "@/components/Elements";
import Loading from "@/components/Loading";
import DashboardContent from "@/components/shared/DashboardContent";
import DashboardContentWrapper from "@/components/shared/DashboardContentWrapper";
import DashboardHeader from "@/components/shared/DashboardHeader";
import { useMainStore } from "@/contexts/Store";
import { ROUTES } from "@/features/risk-assessment";
import { QuestionnaireStatus } from "@/features/risk-assessment/types/questionnaire";

import AssessmentDetailsTabs from "../components/AssessmentDetailsTabs";
import { ModuleContentWrapper } from "../components/ModuleContentWrapper/ModuleContentWrapper";
import ModuleHeader from "../components/ModuleHeader/ModuleHeader";
import PendingQuestionnaireList from "../components/Questionnaires/QuestionnaireList/PendingQuestionnaireList";
import SubmittedQuestionnaireList from "../components/Questionnaires/QuestionnaireList/SubmittedQuestionnaireList";
import { useAssessmentData } from "../hooks";
import { getPartnerName } from "../utils/partners";

function AssessmentPage() {
  const history = useHistory();
  const mainStore = useMainStore();
  const activeWorkspaceId: number | undefined =
    mainStore.context.activeWorkspace?.id;
  const { assessment, assessmentId, fetchAssessmentData } =
    useAssessmentData(activeWorkspaceId);

  if (!assessment) {
    return <Loading loadingLayout="small-table" />;
  }

  const areQuestionnairesPasswordProtected = assessment.questionnaires.some(
    (questionnaire) => {
      return questionnaire.external_password;
    },
  );
  const partnerName = getPartnerName(
    mainStore.riskAssessmentPartners.partners,
    assessment.partner_record_version_id?.toString(),
  );

  const downloadAssessment = () => {
    if (activeWorkspaceId) {
      return AssessmentAPI.downloadAssessment(
        activeWorkspaceId,
        parseInt(assessmentId),
      );
    }
  };

  const pendingQuestionnaires = assessment.questionnaires.filter(
    (q) =>
      q.status === QuestionnaireStatus.NOT_STARTED ||
      q.status === QuestionnaireStatus.INCOMPLETE,
  );
  const submittedQuestionnaires = assessment.questionnaires.filter(
    (q) =>
      q.status &&
      (
        [
          QuestionnaireStatus.READY_FOR_REVIEW,
          QuestionnaireStatus.IN_REVIEW,
          QuestionnaireStatus.FINALIZED,
        ] as string[]
      ).includes(q.status),
  );

  return (
    <DashboardContent>
      <DashboardHeader
        title={`${partnerName} ${assessment?.name || "Assessment"}`.trim()}
        onBackClick={() =>
          history.push(
            generatePath(ROUTES.PARTNERS_ASSESSMENTS_PATH, {
              record_version_id: assessment.partner_record_version_id,
              workspace_id: activeWorkspaceId || -1,
            }),
          )
        }
      />
      <DashboardContentWrapper>
        <ModuleHeader>
          <AssessmentDetailsTabs assessmentId={assessmentId} />
          <ExportBulk
            customFileExportFunction={downloadAssessment}
            isExportFilesEnabled
            isExportSheetEnabled={false}
          />
        </ModuleHeader>
        <ModuleContentWrapper>
          <div className="assessment-page">
            <Flex column rowGap={20}>
              {areQuestionnairesPasswordProtected && (
                <Flex alignCenter columnGap={5}>
                  <Icon name="key" color="brandingDimViolet" size="md" />
                  <Typography
                    label="Password protected"
                    color="generalMidnightDark"
                  />
                  <Flex
                    alignCenter
                    data-tooltip-id="tooltip"
                    data-tooltip-content="All questionnaires in this assessment are password protected. Please remember to copy the link AND the password for the form to be filled out."
                    data-tooltip-place="bottom"
                  >
                    <Icon name="info" />
                  </Flex>
                </Flex>
              )}
              <div>
                <Typography
                  label="Risk Methodology"
                  color="generalMidnightDark"
                  size="lg"
                  weight="semiBold"
                  className="assessment-page__header"
                />
                <Link
                  to={generatePath(ROUTES.METHODOLOGY_EDIT_PATH, {
                    riskMethodologyId: assessment.risk_methodology.id,
                    workspace_id: activeWorkspaceId || -1,
                  })}
                >
                  {assessment.risk_methodology.name}
                </Link>
              </div>
            </Flex>
            {pendingQuestionnaires.length > 0 && (
              <div>
                <Typography
                  label="Pending Questionnaires"
                  color="generalMidnightDark"
                  size="lg"
                  weight="semiBold"
                  className="assessment-page__header"
                />
                <PendingQuestionnaireList
                  questionnaires={pendingQuestionnaires}
                  assessment={assessment}
                  onRefreshAssessment={fetchAssessmentData}
                />
              </div>
            )}
            {submittedQuestionnaires.length > 0 && (
              <div>
                <Typography
                  label="Submitted Questionnaires"
                  color="generalMidnightDark"
                  size="lg"
                  weight="semiBold"
                  className="assessment-page__header"
                />
                <SubmittedQuestionnaireList
                  questionnaires={submittedQuestionnaires}
                  assessment={assessment}
                  onRefreshAssessment={fetchAssessmentData}
                />
              </div>
            )}
          </div>
        </ModuleContentWrapper>
      </DashboardContentWrapper>
    </DashboardContent>
  );
}

export default observer(AssessmentPage);
